import { contactProfileLookUP } from "app/redux/features/contact-profile-lookup";
import FilePreview from "app/uicomponents/macrocomponents/ProposalPending/FilePreview";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { getLocalStore } from "app/utils/localStore";
import { customFormatDate } from "app/utils/luxon";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore, setSessionStore } from "app/utils/sessionStore";
import { THEME, TOASTTYPE, USERTYPE } from "app/utils/types";
import { footerHeight } from "app/utils/utils";
import { CopilotWidget, Root } from "iapp-neio";
import { Toast } from "primereact/toast";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { neioMakeRequest } from "../utils/neioMakeRequest";

interface IChat {
  user: any;
  peers: any[];
  children?: any;
  activeConversationId?: number | null;
  neioAccessToken?: string;
}

const Chat: FC<IChat> = ({ peers, user, children, activeConversationId, neioAccessToken }) => {
  const { data: lookUpData } = useSelector(contactProfileLookUP);
  const { branding } = lookUpData;
  const { primaryColor = "" } = branding ?? {};

  const [fileData, setFileData] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [userChats, setUserChats] = useState<any[]>([]);
  const [starterTopics, setStarterTopics] = useState<any[]>([]);
  const { userType } = getLocalStore("useraccess");

  const downloadAndSaveFile = (url: string, name?: string) =>
    fetch(url, { method: "get" })
      .then((response) => response.blob())
      .then((response) => {
        const href = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", name || "doc");
        document.body.appendChild(link);
        link.click();
      });

  useEffect(() => {
    const fetchData = async () => {
      const filteredUser = peers.find((el) => el.userType === "bot");
      if (filteredUser) {
        const query = `${filteredUser?.id}`;
        try {
          const response = await neioMakeRequest(
            "neioUploadFile",
            {},
            { headers: { organization: "iapp" } },
          ).get(query, true, false);

          const apiData = response?.data?.data;
          const starterTopic = apiData.starter_topics.map((item: any) => ({
            topic: item.body_text,
            subTopic: item.sub_body_text,
          }));

          setStarterTopics(starterTopic);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error fetching data:", error);
        }
      }
    };
    fetchData();
  }, []);

  const uploadFile = (file: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => {
    const formData = new FormData();
    formData.append("file", file);

    return makeRequest(
      "chatFileUpload",
      {
        "Content-Type": "multipart/form-data",
      },
      {
        onUploadProgress,
      },
    )
      .post(formData)
      .then((res) => res.data.Key)
      .catch((err) => {
        if (err.message?.includes("image\\/png|image\\/jpeg|image\\/jpg|application\\/pdf")) {
          throw new Error("Please try uploading PDF/Word document/PNG/ JPG/JPEG files.");
        }
        throw new Error(err?.message ?? err);
      });
  };

  const deleteFile = (fileId: string) =>
    makeRequest("chatFileRemove").delete(encodeURIComponent(fileId), true) as any;

  const downloadFile = (fileId: string) =>
    makeRequest("chatFile")
      .get(encodeURIComponent(fileId), true)
      .then((res) => {
        res.data.data &&
          downloadAndSaveFile(res.data.data, fileId.substring(fileId.lastIndexOf("/") + 1));
      });

  const getPresignedUrl = (fileId: any) =>
    makeRequest("chatFile")
      .get(encodeURIComponent(fileId), true)
      .then((res) => res.data.data);

  const { pathname } = useLocation();
  const globalTheme = getSessionStore("globalTheme");
  const { isDarkMode: checked } = useContext(ThemeContext);

  const onMessageReceived = (message: any) => {
    const sender = peers.find((peer) => peer.id === message?.data?.sender);
    if (!sender) {
      return;
    }

    const otherColor = userType === USERTYPE.CONTACT ? primaryColor || "#002446" : "#002446";

    if (message?.data?.message) {
      const msg = (message?.data?.message || "") as string;
      toast?.current?.show({
        severity: TOASTTYPE.INFO,
        content: (
          <div
            onClick={() => {
              const chatIcon = document.querySelector<HTMLSpanElement>(".chat-icon");

              if (chatIcon) {
                setSessionStore("showChatDot", false);
                chatIcon.classList.add("hidden");
              }
              navigate("/chat");
            }}
          >
            <div className="font-bold">
              {message?.data?.data?.externalId
                ? ""
                : `New Message from ${sender?.firstName} ${sender?.lastName}`}
            </div>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: message?.data?.data?.externalId
                  ? `${sender?.lastName}, ${sender?.firstName} has shared a file.`
                  : msg.substring(0, 20) + (msg.length > 20 ? "..." : ""),
              }}
            />
          </div>
        ),
        life: 3000,
        contentStyle: {
          background: "#fff",
          color: otherColor,
          borderColor: otherColor,
          borderLeftColor: otherColor,
        },
        style: {
          borderColor: otherColor,
          borderLeftColor: otherColor,
        },
        contentClassName: "justify-between",
        closeIcon: <i className="pi pi-times" style={{ color: primaryColor || "#002446" }} />,
        className: "toast-chat-wrapper",
      });
    }

    const chatIcon = document.querySelector<HTMLSpanElement>(".chat-icon");

    if (chatIcon) {
      setSessionStore("showChatDot", true);
      chatIcon.classList.remove("hidden");
    }
  };

  const onFileClick = (fileId: string) => {
    const fileName = fileId.substring(fileId.lastIndexOf("/") + 1);

    makeRequest("chatFile")
      .get(encodeURIComponent(fileId), true)
      .then((res) => {
        if (res.data.data) {
          const url = res.data.data;
          setFileData({
            name: fileName ?? "",
            url,
          });
        }
      });
  };

  useEffect(() => {
    const unreadCount = userChats
      .filter((el) => {
        const p1 = el?.user_conversations?.[0]?.participant_id;
        const p2 = el?.user_conversations?.[1]?.participant_id;

        let userToSearch = "";
        if (user.id === p1) {
          userToSearch = p2;
        } else {
          userToSearch = p1;
        }

        return peers.findIndex((p) => p.id === userToSearch) >= 0;
      })
      .reduce((acc, ch) => {
        if (ch.unread_count > 0) {
          acc += ch.unread_count;
        }
        return acc;
      }, 0);

    const chatIcon = document.querySelector<HTMLSpanElement>(".chat-icon");

    if (chatIcon && userChats.length > 0) {
      if (unreadCount > 0) {
        setSessionStore("showChatDot", true);
        chatIcon.classList.remove("hidden");
      } else {
        setSessionStore("showChatDot", false);
        chatIcon.classList.add("hidden");
      }
    }
  }, [userChats, user, peers]);

  return (
    <div className="chat-root">
      <Root
        options={{
          apiKey: neioAccessToken || "",
          // apiUrl: "https://dev-core.neio.ai/v1",
          apiUrl: "https://dev.neio.core.iapp.cool/v1",
          // apiDomain: "dev-websocket.neio.ai",
          apiDomain: "neio.wss.iapp.cool",
          // socketUrl: "ws://44.223.214.195:8080",
          socketUrl: "wss://neio.wss.iapp.cool",
          user,
          peers: peers.filter((el) => el?.id),
          fileOptions: {
            downloadOption: "direct",
            uploadFile,
            deleteFile,
            downloadFile,
            getPresignedUrl,
            onClick: onFileClick,
          },
          messageTimeFormat(timestamp, shouldCheckPastDate) {
            const date = new Date(timestamp);
            const todayDate = new Date();

            if (shouldCheckPastDate && todayDate.toDateString() !== date.toDateString()) {
              return customFormatDate(timestamp, "MMM d", undefined);
            }

            return customFormatDate(timestamp, "hh:mm a", undefined, "CST");
          },
          organization: "iapp",
          activeConversationId,
          userType: userType as USERTYPE,
          headless: pathname !== "/chat",
          onMessageReceived,
          starterTopic: starterTopics,
          features: {
            chatWithUsers: true,
            chatWithBot: false,
            chatWithGroup: false,
          },
          onLoadComplete(data) {
            setUserChats(data ?? []);
          },
          onMessageStatusUpdates(message) {
            if (message?.type === "message.new") {
              const chatIcon = document.querySelector<HTMLSpanElement>(".chat-icon");

              if (chatIcon) {
                setSessionStore("showChatDot", true);
                chatIcon.classList.remove("hidden");
              }
            } else {
              setUserChats((p: any[]) =>
                p?.map((el) => {
                  if (el?.id === message?.conversation_id) {
                    return {
                      ...el,
                      unread_count: 0,
                    };
                  }
                  return { ...el };
                }),
              );
            }
          },
          theme: {
            defaultTheme: {
              closeIcon: {},
            },
            customTheme: {
              ...(userType === USERTYPE.CONTACT
                ? {
                    messageSectionHeight: `calc(100vh - 185px - ${footerHeight}rem)`,
                    listSectionHeight: `calc(100vh - 320px - ${footerHeight}rem)`,
                    messageSectionWidth: "calc(100vw - 568px)",

                    loaderWrapperStyle: {
                      height: `calc(100vh - ${footerHeight}rem - 11.875rem)`,
                    },
                    blankStateWrapperStyle: {
                      height: `calc(100vh - ${footerHeight}rem - 11.875rem)`,
                    },
                  }
                : {
                    listSectionHeight: "calc(100vh - 18.375rem)",
                    messageSectionHeight: "calc(100vh - 6.5625rem)",
                    messageSectionWidth: `calc(100vw - ${footerHeight}rem))`,
                    loaderWrapperStyle: {
                      height: `calc(100vh - ${footerHeight}rem - 4.125rem)`,
                    },
                    moreOptionsIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    blankStateWrapperStyle: {
                      height: `calc(100vh - ${footerHeight}rem - 4.125rem)`,
                    },
                  }),
              ...(globalTheme === THEME.DARK || checked
                ? {
                    chatWindow: {
                      color: {
                        backgroundColor: "#0b213f",
                      },
                    },
                    peopleSearchBarChat: {
                      fontColor: {
                        searchTextColor: "#0b213f",
                      },
                      color: {
                        backgroundColor: "#0b213f",
                      },
                    },
                    searchedList: {
                      color: {
                        searchedListBg: "rgb(7, 20, 38)",
                        unreadMessagesBg: "#FF00ff",
                        selectedChatBg: "#f1f",
                      },
                      fontColor: {
                        personNameTextColor: "#fff",
                        lastMessageTextColor: "#fff",
                      },
                    },
                    listOfPeopleForChat: {
                      fontColor: {
                        noResultsFoundTextColor: "#fff",
                      },
                      color: {
                        backgroundColor: "rgb(255,205,255)",
                      },
                    },

                    AddNewChatModalTheme: {
                      color: {
                        headerBorder: "#71717A",
                        backgroundColor: "rgb(7, 20, 38)",
                        cancelIconColor: "#71717A",
                      },
                      fontColor: {
                        titleTextColor: "#fff",
                        subTitleTextColor: "#fff",
                        inputTextColor: "#fff",
                      },
                    },
                    searchedChat: {
                      color: {
                        chevronColor: "#fff",
                        searchedBorderColor: "#ff0000",
                      },
                      fontColor: {
                        textColor: "#fff",
                        emailColor: "#fff",
                        noMatchesFoundText: "#fff",
                      },
                    },

                    profileDisplay: {
                      color: {
                        backgroundColor: "rgb(7, 20, 38)",
                        titleBg: "#0b213f",
                      },
                      fontColor: {
                        titleColor: "#fff",
                      },
                    },
                    moreOptionsIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    blockModal: {
                      color: {
                        backgroundColor: "rgb(7, 20, 38)",
                        actionButtonBgColor: "#e24c4c",
                        cancelButtonBorder: "#71717A",
                        blockMenuBgColor: "#fff",
                        modalActionButtonBgColor: "#e24c4c",
                      },
                      fontColor: {
                        modalTitleColor: "#fff",
                        modalSubtitleColor: "#fff",
                        cancelTextColor: "#fff",
                        actionButtonTextColor: "#fff",
                      },
                    },

                    textArea: {
                      color: {
                        backgroundColor: "rgb(7, 20, 38)",
                      },
                      fontColor: {
                        typedTextColor: "#fff",
                      },
                    },
                    chatUploader: {
                      color: {
                        backgroundColor: "rgb(7, 20, 38)",
                      },
                      fontColor: {
                        fileNameTextColor: "#fff",
                      },
                    },
                    dateLine: {
                      dateTextStyle: {
                        color: "#bdbdbd",
                      },
                      dividerStyle: {
                        borderColor: "#8f8f8f",
                      },
                    },

                    sentMessage: {
                      color: {
                        sentMessagePrimaryBackgroundColor: "rgb(7, 20, 38)",
                        sentMessageSecondaryBackgroundColor: "#F11",
                        sentMessageTextColor: "#fff",
                      },
                      sentMessageStatusStyle: {
                        filter: "invert(1)",
                      },
                    },
                    replyMessage: {
                      color: {
                        replyMessagePrimaryBackgroundColor: "rgb(17, 40, 68)",
                        replyMessageSecondaryBackgroundColor: "#f11",
                        replyMessageTextColor: "#fff",
                      },
                    },
                    fileMessage: {
                      color: {
                        fileMessagePrimaryBackgroundColor: "#0e1b2c",
                        fileMessageSecondaryBackgroundColor: "#f11",
                        fileMessageTextColor: "#fff",
                      },
                    },

                    unsentMessage: {
                      color: {
                        unsentMessagePrimaryBackgroundColor: "rgb(175, 82, 82)",
                        unsentMessageRetryTextColor: "#fff",
                        unsentMessageSecondaryBackgroundColor: "#ff1",
                        unsentMessageTextColor: "#ff6259",
                        unsentMessageWarningTextColor: "#ff6259",
                      },
                    },

                    editMessageMenu: {
                      color: {
                        backgroundColor: "#1c1d20",
                        editIconColor: "#fff",
                        blockIconColor: "#fff",
                      },
                      fontColor: {
                        deleteTextColor: "#e24c4c",
                        EditTextColor: "#fff",
                      },
                    },

                    peopleSearchBox: {
                      color: {
                        borderColor: "red",
                        // borderColor: "rgb(7, 20, 38)",
                        textColor: "#f1f",
                        searchButtonColorBg: "#1581ED",
                        seachButtonBorder: "#1581ED",
                        backgroundColor: "rgb(210,111,25)",
                      },
                    },
                    blockIcon: {
                      color: {
                        iconColor: "#f1f",
                      },
                    },
                    fileLimitModal: {
                      color: {
                        backgroundColor: "#F1F",
                        buttonBgColor: "#1581ED",
                        buttonBorderColor: "#3B82F6",
                      },
                      fontColor: {
                        contentTextColor: "#6B7280",
                        buttonLabelColor: "#FFFFFF",
                      },
                    },
                    fileIcon: {
                      color: {
                        iconColor: "#fafafa",
                      },
                    },
                    downloadIcon: {
                      color: {
                        iconColor: "#fafafa",
                      },
                    },
                    moreVerticalIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    uploadIcon: {
                      color: {
                        arrowIconColor1: "#fff",
                        arrowIconColor2: "#fff",
                        baseIconColor: "#fff",
                      },
                    },
                    editIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    searchIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    doneIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    closeIcon: {
                      color: {
                        iconColor: "#fff",
                      },
                    },
                    closeUploadIcon: {
                      color: {
                        iconColor: "#fff",
                        rectColor: "#1c1d20",
                      },
                    },
                  }
                : {
                    peopleSearchBarChat: {
                      color: {
                        backgroundColor: "#0b213f",
                      },
                    },
                    sentMessage: {
                      color: {
                        sentMessagePrimaryBackgroundColor: "#e5e7eb",
                        sentMessageTextColor: "#000",
                      },
                    },
                  }),
            },
          },
        }}
      >
        <Toast ref={toast} position="top-right" className="cursor-pointer" />
        <CopilotWidget />
        {children}
        {fileData && (
          <FilePreview
            onCancel={() => {
              setFileData(null);
            }}
            url={fileData?.url || ""}
            loading
            docCategory={fileData?.name || ""}
          />
        )}
      </Root>
    </div>
  );
};

export default Chat;
